/**
 * The welcome pack beyond the business card — door hanger, car magnet,
 * yard sign.
 *
 * Same spec-sheet language as the card (stock #fdfcfa, ink #1e2228, hairline
 * rules, mono eyebrow) and the same `PackBusiness` identity: real logo,
 * logoFont/heading Google Fonts and brand colour, exactly as the deployed
 * site wears them.
 *
 * Print targets (300 DPI export happens at conversion; `bleed` grows the piece
 * here so the print entry can render the same component with bleed):
 *   door hanger  4.25in × 11in  — die-cut hole ⌀1.25in, centre 1.05in from top
 *   car magnet   18in × 12in    — 0.5in rounded corners, 0.5in safe
 *   yard sign    24in × 18in    — coroplast, 0.75in safe
 *
 * The magnet is 18×12 because that is the product we actually source
 * (docs/welcome-pack-sourcing.md §1/§3 — BuildASign 12×18, ~$13). It used to
 * be drawn 24×12; the mockup now shows what ships.
 *
 * Each item renders at its own screen PPI: the hanger is small enough to show
 * near print scale, the magnet and sign are big-format and shrink to 24 px/in
 * so type sized for street distance still fits a browser.
 */

import { useState } from 'react';
import {
  facePalette,
  LogoImage,
  Qr,
  resolveLogo,
  showsName,
  siteFont,
  type LogoMode,
  type NameMode,
  type PackBusiness,
} from './BusinessCard';

/* ------------------------------------------------------------------ */
/* Geometry                                                            */
/* ------------------------------------------------------------------ */

export const HANGER_PPI = 60;
export const HANGER_W = 4.25 * HANGER_PPI; // 255
export const HANGER_H = 11 * HANGER_PPI; // 660
const HANGER_SAFE = 0.1875 * HANGER_PPI;
/** Exported so a plate can punch the same die-cut through its overlay mask. */
export const HOLE_R = (1.25 / 2) * HANGER_PPI;
export const HOLE_CY = 1.05 * HANGER_PPI;

export const MAGNET_PPI = 24;
/** 18 × 12 in — the sourced BuildASign product, not a 24in door-length panel. */
export const MAGNET_W = 18 * MAGNET_PPI; // 432
export const MAGNET_H = 12 * MAGNET_PPI; // 288
const MAGNET_SAFE = 0.5 * MAGNET_PPI;
const MAGNET_RADIUS = 0.5 * MAGNET_PPI;

export const SIGN_PPI = 24;
export const SIGN_W = 24 * SIGN_PPI; // 576
export const SIGN_H = 18 * SIGN_PPI; // 432
const SIGN_SAFE = 0.75 * SIGN_PPI;

export interface PackItemProps {
  b: PackBusiness;
  guides: boolean;
  nameMode: NameMode;
  logoMode: LogoMode;
  /** Where the QR points — the live site. Null prints the lab's static art. */
  qrUrl?: string | null;
  /**
   * Render at this size instead of the item's natural footprint. The internals
   * are flex-based, so the piece re-lays-out around the new box exactly like a
   * resized div: type keeps its designed px, centred content stays centred.
   * Used by the photo plate's `reflow` fit.
   */
  w?: number;
  h?: number;
  /**
   * Print only: grow the painted box by this many component px on every side
   * so stock and bled elements run past the trim line. Zero on screen, and
   * with zero every piece is byte-for-byte what it always was.
   */
  bleed?: number;
  /** Print only: emit QR codes as inline SVG rather than a raster. */
  qrVector?: boolean;
  /**
   * Door hanger only: the surface behind punches a real transparent hole (the
   * photo plate does) at this geometry, in the hanger's own px. Its presence
   * means don't paint the simulated paper hole — two holes in slightly
   * different places is exactly what reads as broken — and the content clears
   * the real hole instead of the printed one, which is bigger on every plate.
   */
  holeCut?: { cy: number; ry: number };
}

/* ------------------------------------------------------------------ */
/* Shell                                                               */
/* ------------------------------------------------------------------ */

function ItemShell({
  w,
  h,
  safe,
  safeLabel,
  guides,
  radius = 4,
  background = '#fdfcfa',
  bleed = 0,
  children,
}: {
  w: number;
  h: number;
  safe: number;
  safeLabel: string;
  guides: boolean;
  radius?: number;
  background?: string;
  bleed?: number;
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        'relative overflow-hidden' +
        // On a press sheet the cast shadow would print as a grey smear, and a
        // corner radius is the TRIM shape (the magnet's rounded corners are
        // cut, not printed) — the artwork itself has to stay a full rectangle.
        (bleed ? '' : ' shadow-[0_1px_3px_rgba(30,34,40,0.12),0_8px_24px_rgba(30,34,40,0.10)]')
      }
      style={{
        width: w + bleed * 2,
        height: h + bleed * 2,
        background,
        borderRadius: bleed ? 0 : radius,
        isolation: 'isolate',
      }}
    >
      {children}
      {guides ? (
        <div className="pointer-events-none absolute inset-0 z-20">
          <div
            className="absolute border border-dashed border-fuchsia-500/70"
            style={{ inset: safe + bleed }}
          />
          <span className="absolute left-1/2 top-0.5 -translate-x-1/2 whitespace-nowrap font-mono text-[8px] uppercase tracking-[0.14em] text-fuchsia-600">
            {safeLabel}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Door hanger — 4.25 × 11 in                                          */
/* ------------------------------------------------------------------ */

export function DoorHanger({
  b,
  guides,
  nameMode,
  logoMode,
  qrUrl,
  w,
  h,
  holeCut,
  bleed = 0,
  qrVector,
}: PackItemProps) {
  const [broken, setBroken] = useState(false);
  const logo = broken ? null : resolveLogo(b, logoMode);
  const name = showsName(broken ? { ...b, logo: null } : b, nameMode);
  // The stock and its inks — the logo's own colour when it ships on a solid
  // canvas, plain paper otherwise (see facePalette in BusinessCard).
  const p = facePalette(b, logo);
  const onSurface = p.surface !== null;
  return (
    <ItemShell
      w={w ?? HANGER_W}
      h={h ?? HANGER_H}
      safe={HANGER_SAFE}
      safeLabel="safe area 0.1875in"
      guides={guides}
      background={p.bg}
      bleed={bleed}
    >
      {/* Die-cut hole — punched through, shows the page behind. Skipped when
          the surface cuts a real one at its own measured position, and on the
          print sheet, where the hole is a cutting instruction and the artwork
          under it must stay printed. */}
      {holeCut || bleed ? null : (
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-full"
          style={{
            top: HOLE_CY - HOLE_R,
            width: HOLE_R * 2,
            height: HOLE_R * 2,
            background: '#f6f4ef',
            boxShadow: 'inset 0 1px 4px rgba(30,34,40,0.28)',
          }}
        />
      )}
      {guides ? (
        <div
          className="absolute left-1/2 border-l border-dashed border-fuchsia-500/70"
          style={{ top: 0, height: bleed + HOLE_CY - HOLE_R }}
        />
      ) : null}

      <div
        className="flex h-full flex-col"
        style={{
          paddingLeft: HANGER_SAFE + 4 + bleed,
          paddingRight: HANGER_SAFE + 4 + bleed,
          paddingBottom: HANGER_SAFE + 4 + bleed,
          paddingTop: (holeCut ? holeCut.cy + holeCut.ry : bleed + HOLE_CY + HOLE_R) + 14,
        }}
      >
        <div
          className="flex items-baseline justify-between border-b pb-2"
          style={{ borderColor: p.rule }}
        >
          <span
            className="font-mono text-[8px] uppercase tracking-[0.18em]"
            style={{ color: p.muted }}
          >
            {b.trade}
            {b.city ? ` · ${b.city}` : ''}
          </span>
          {b.established ? (
            <span
              className="font-mono text-[8px] uppercase tracking-[0.18em]"
              style={{ color: p.faint }}
            >
              Est. {b.established}
            </span>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
          {logo ? (
            <LogoImage
              src={logo}
              align="center"
              imgClass={`${name ? 'max-h-[56px]' : 'max-h-[76px]'} max-w-[200px] object-contain`}
              onError={() => setBroken(true)}
              darkBackground={b.logoDarkBackground}
              onSurface={onSurface}
            />
          ) : null}
          {name ? (
            <div
              className={`font-bold ${logo ? 'text-[15px] leading-tight' : 'text-[21px] leading-[1.05]'}`}
              style={{ ...siteFont(b, 'logo'), color: p.ink }}
            >
              {b.name}
            </div>
          ) : null}
          {b.owner ? (
            <div className="text-[11px]" style={{ color: p.muted }}>
              {b.owner}
            </div>
          ) : null}
        </div>

        <div className="text-center">
          <div
            className="text-[21px] font-bold leading-snug"
            style={{ ...siteFont(b, 'heading'), color: p.ink }}
          >
            {b.trade}, right around the corner.
          </div>
          <p
            className="mx-auto mt-2 max-w-[200px] text-[11px] leading-relaxed"
            style={{ color: p.muted }}
          >
            Call for a quick quote, or scan to see our work.
          </p>
        </div>

        <div className="mt-5 border-t pt-3 text-center" style={{ borderColor: p.rule }}>
          <div
            className="text-[19px] font-bold"
            style={{ ...siteFont(b, 'heading'), color: p.ink }}
          >
            {b.phone}
          </div>
          <div className="mt-0.5 font-mono text-[10px]" style={{ color: p.primary }}>
            {b.domain}
          </div>
        </div>

        <div className="mt-4 flex flex-col items-center gap-1.5 pb-2">
          <span className="rounded-[3px] bg-white p-2 ring-1 ring-zinc-200">
            <Qr size={92} url={qrUrl} vector={qrVector} />
          </span>
          <span
            className="font-mono text-[8px] uppercase tracking-[0.18em]"
            style={{ color: p.faint }}
          >
            Our work &amp; reviews
          </span>
        </div>
      </div>
      <div
        className="absolute inset-x-0 bottom-0"
        style={{ height: 4 + bleed, background: p.accent }}
      />
    </ItemShell>
  );
}

/* ------------------------------------------------------------------ */
/* Car magnet — 18 × 12 in                                             */
/* ------------------------------------------------------------------ */

export function CarMagnet({ b, guides, nameMode, logoMode, w, h, bleed = 0 }: PackItemProps) {
  const [broken, setBroken] = useState(false);
  const logo = broken ? null : resolveLogo(b, logoMode);
  const name = showsName(broken ? { ...b, logo: null } : b, nameMode);
  // The magnet's stock is plain white; on a solid-canvas logo it takes the
  // logo's own colour (see facePalette in BusinessCard).
  const p = facePalette(b, logo, '#ffffff');
  const onSurface = p.surface !== null;
  return (
    <ItemShell
      w={w ?? MAGNET_W}
      h={h ?? MAGNET_H}
      safe={MAGNET_SAFE}
      safeLabel="safe area 0.5in"
      guides={guides}
      radius={MAGNET_RADIUS}
      background={p.bg}
      bleed={bleed}
    >
      <div
        className="flex h-full flex-col items-center justify-center gap-2 text-center"
        style={{ padding: MAGNET_SAFE + bleed }}
      >
        <span
          className="font-mono text-[10px] uppercase tracking-[0.22em]"
          style={{ color: p.muted }}
        >
          {b.trade}
          {b.city ? ` · ${b.city}` : ''}
        </span>
        <div className="flex flex-col items-center gap-1.5">
          {logo ? (
            <LogoImage
              src={logo}
              align="center"
              imgClass={`${name ? 'max-h-[64px]' : 'max-h-[92px]'} max-w-[380px] object-contain`}
              onError={() => setBroken(true)}
              darkBackground={b.logoDarkBackground}
              onSurface={onSurface}
            />
          ) : null}
          {name ? (
            <div
              className={`font-bold leading-[1.05] ${logo ? 'text-[26px]' : 'text-[36px]'}`}
              style={{ ...siteFont(b, 'logo'), color: p.ink }}
            >
              {b.name}
            </div>
          ) : null}
        </div>
        {/* The phone is the whole point of a magnet — largest thing on it. */}
        <div
          className="mt-1 text-[44px] font-bold leading-none"
          style={{ ...siteFont(b, 'heading'), color: p.ink }}
        >
          {b.phone}
        </div>
        <div className="font-mono text-[14px]" style={{ color: p.primary }}>
          {b.domain}
        </div>
      </div>
      <div
        className="absolute inset-x-0 bottom-0"
        style={{ height: 10 + bleed, background: p.primary }}
      />
    </ItemShell>
  );
}

/* ------------------------------------------------------------------ */
/* Yard sign — 24 × 18 in                                              */
/* ------------------------------------------------------------------ */

export function YardSign({
  b,
  guides,
  nameMode,
  logoMode,
  qrUrl,
  w,
  h,
  bleed = 0,
  qrVector,
}: PackItemProps) {
  const [broken, setBroken] = useState(false);
  const logo = broken ? null : resolveLogo(b, logoMode);
  const name = showsName(broken ? { ...b, logo: null } : b, nameMode);
  // The stock and its inks — the logo's own colour when it ships on a solid
  // canvas, plain paper otherwise (see facePalette in BusinessCard).
  const p = facePalette(b, logo);
  const onSurface = p.surface !== null;
  return (
    <ItemShell
      w={w ?? SIGN_W}
      h={h ?? SIGN_H}
      safe={SIGN_SAFE}
      safeLabel="safe area 0.75in"
      guides={guides}
      radius={3}
      background={p.bg}
      bleed={bleed}
    >
      {/* Brand frame — the sign's one big colour move. Inset from the TRIM, so
          on a bleeding sheet it moves in by the bleed rather than staying put
          and reading as an off-centre border after the cut. */}
      <div
        className="pointer-events-none absolute border-[6px]"
        style={{ inset: 10 + bleed, borderColor: p.primary }}
      />
      <div
        className="flex h-full flex-col items-center justify-center gap-2.5 text-center"
        style={{ padding: SIGN_SAFE + bleed }}
      >
        <span
          className="font-mono text-[10px] uppercase tracking-[0.22em]"
          style={{ color: p.muted }}
        >
          {b.trade}
          {b.city ? ` · ${b.city}` : ''}
          {b.established ? ` · est. ${b.established}` : ''}
        </span>
        <div className="flex flex-col items-center gap-1.5">
          {logo ? (
            <LogoImage
              src={logo}
              align="center"
              imgClass={`${name ? 'max-h-[80px]' : 'max-h-[110px]'} max-w-[380px] object-contain`}
              onError={() => setBroken(true)}
              darkBackground={b.logoDarkBackground}
              onSurface={onSurface}
            />
          ) : null}
          {name ? (
            <div
              className={`font-bold leading-[1.05] ${logo ? 'text-[28px]' : 'text-[38px]'}`}
              style={{ ...siteFont(b, 'logo'), color: p.ink }}
            >
              {b.name}
            </div>
          ) : null}
        </div>
        <div
          className="mt-1 text-[48px] font-bold leading-none"
          style={{ ...siteFont(b, 'heading'), color: p.ink }}
        >
          {b.phone}
        </div>
        <div className="font-mono text-[15px]" style={{ color: p.primary }}>
          {b.domain}
        </div>
      </div>
      <div
        className="absolute flex flex-col items-center gap-1"
        style={{ bottom: 24 + bleed, right: 24 + bleed }}
      >
        <span className="rounded-[3px] bg-white p-1.5 ring-1 ring-zinc-200">
          <Qr size={56} url={qrUrl} vector={qrVector} />
        </span>
        <span
          className="font-mono text-[7px] uppercase tracking-[0.14em]"
          style={{ color: p.faint }}
        >
          Free quote
        </span>
      </div>
    </ItemShell>
  );
}
